<template>
  <div>
    <div class="kb-row">
      <div>
        <h4 class="kb-row-title">
          Golden Wookiee
        </h4>
        <p class="kb-row-hint">
          Awarded each year to members who went above and beyond for the krewe
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdating"
          label="Saving"
        />
        <ToggleSwitch
          v-model="isGoldenWookiee"
          :disabled="isUpdating"
          aria-label="Golden Wookiee"
          @change="onStatusChange"
        />
      </div>

      <div
        v-if="isGoldenWookiee && (member.golden_wookiee_year || member.golden_wookiee_reason)"
        class="kb-row-body"
      >
        <p
          v-if="member.golden_wookiee_reason"
          class="kb-row-note"
        >
          {{ member.golden_wookiee_reason }}
        </p>
        <p
          v-if="member.golden_wookiee_year"
          class="kb-row-meta m-0"
        >
          Awarded {{ member.golden_wookiee_year }}
        </p>
      </div>
    </div>

    <Dialog
      :visible="showDialog"
      modal
      header="Award a Golden Wookiee"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :closable="false"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          You're about to award <strong>{{ member.name || 'this member' }}</strong> a Golden Wookiee. It goes each year to members who made outstanding contributions to the krewe.
        </p>

        <div class="kb-field">
          <label for="golden-wookiee-year">Year</label>
          <InputNumber
            v-model="awardYear"
            input-id="golden-wookiee-year"
            :use-grouping="false"
            :min="2000"
            :max="2100"
            size="small"
            fluid
            :invalid="!!yearError"
            placeholder="Award year"
          />
          <small
            v-if="yearError"
            class="kb-field-error"
          >{{ yearError }}</small>
        </div>

        <!-- The year comes filled in, so the reason is what's left to type -->
        <div class="kb-field">
          <label for="golden-wookiee-reason">Reason</label>
          <InputText
            id="golden-wookiee-reason"
            v-model="awardReason"
            size="small"
            fluid
            autofocus
            :invalid="!!reasonError"
            aria-describedby="golden-wookiee-reason-error"
            placeholder="Short reason for the award"
          />
          <small
            v-if="reasonError"
            id="golden-wookiee-reason-error"
            class="kb-field-error"
          >{{ reasonError }}</small>
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          @click="cancel"
        />
        <Button
          label="Award Golden Wookiee"
          @click="confirm"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { Member } from '~/shared/types/member'

interface Props {
  member: Member
}

const props = defineProps<Props>()

const membersStore = useMembersStore()
const toast = useToast()

const isGoldenWookiee = ref(Boolean(props.member.golden_wookiee))
const isUpdating = ref(false)
const showDialog = ref(false)
const awardYear = ref<number | null>(new Date().getFullYear())
const awardReason = ref('')
const yearError = ref('')
const reasonError = ref('')

watch(() => props.member.golden_wookiee, (newValue) => {
  isGoldenWookiee.value = Boolean(newValue)
}, { immediate: true })

const onStatusChange = () => {
  if (isGoldenWookiee.value) {
    awardYear.value = typeof props.member.golden_wookiee_year === 'number'
      ? props.member.golden_wookiee_year
      : new Date().getFullYear()
    awardReason.value = typeof props.member.golden_wookiee_reason === 'string'
      ? props.member.golden_wookiee_reason
      : ''
    showDialog.value = true
  }
  else {
    updateStatus(false)
  }
}

const cancel = () => {
  isGoldenWookiee.value = false
  showDialog.value = false
  awardReason.value = ''
  yearError.value = ''
  reasonError.value = ''
}

const confirm = () => {
  yearError.value = ''
  reasonError.value = ''

  if (!awardYear.value) {
    yearError.value = 'A year is required'
  }
  if (!awardReason.value.trim()) {
    reasonError.value = 'A reason is required'
  }
  if (yearError.value || reasonError.value) return

  showDialog.value = false
  updateStatus(true, awardYear.value ?? undefined, awardReason.value)
}

const updateStatus = async (granted: boolean, year?: number, reason?: string) => {
  isUpdating.value = true
  try {
    const success = await membersStore.toggleGoldenWookieeStatus(props.member, granted, year, reason)
    if (success) {
      toast.add({
        severity: 'success',
        summary: granted ? 'Golden Wookiee Awarded' : 'Golden Wookiee Removed',
        detail: granted
          ? `${props.member.name} has been awarded a Golden Wookiee`
          : `Golden Wookiee has been removed from ${props.member.name}`,
        life: 3000,
      })
    }
    else {
      isGoldenWookiee.value = !granted
      toast.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: `Could not ${granted ? 'award' : 'remove'} Golden Wookiee`,
        life: 3000,
      })
    }
  }
  catch (error) {
    isGoldenWookiee.value = !granted
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdating.value = false
    awardReason.value = ''
  }
}
</script>
